import React from 'react';
import { toast } from 'sonner';
import { industrialProductsAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';
import {
    Package,
    Plus,
    Trash2,
    ChevronDown,
    Camera,
    X,
    ImageIcon,
    Images,
    Loader2,
    Eye,
    EyeOff,
    PlusCircle,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ImageCropDialog from '@/components/ImageCropDialog';
import ImagePickerDialog from '@/components/ImagePickerDialog';
import PriceHistoryPanel from '@/components/PriceHistoryPanel';
import { EU_ALLERGENS, FOOD_FAMILIES, isFoodFamily } from './productsConstants';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

export default function ProductsDialogs(props) {
    const { user } = useAuth();
    const canEditCompanies = user && (user.role === 'admin' || user.role === 'supervisor');
    const {
        // Data
        products, suppliers, categories, setCategories, familles, setFamilles,
        // Industrial Product Dialog
        productDialog, setProductDialog,
        editingProduct,
        productForm, setProductForm,
        showNewCategoryInput, setShowNewCategoryInput,
        newCategoryName, setNewCategoryName,
        showNewFamilleInput, setShowNewFamilleInput,
        newFamilleName, setNewFamilleName,
        famillePopoverOpen, setFamillePopoverOpen,
        hideUnselectedAllergens, setHideUnselectedAllergens,
        imagePreview, imageFile, showCropDialog, setShowCropDialog, cropImageSrc,
        handleImageSelect, handleCropDone, handleImageRemove,
        showImagePicker, setShowImagePicker, handlePickExistingImage,
        handleSaveProduct,
        // Supplier Product Dialog
        supplierProductDialog, setSupplierProductDialog,
        editingSupplierProduct,
        supplierProductForm, setSupplierProductForm,
        spImagePreview, spImageFile, spShowCropDialog, setSpShowCropDialog, spCropImageSrc, setSpCropImageSrc,
        setSpImagePreview, setSpImageFile, setSpImageDeleted, setSpImageSourceRef,
        showSpImagePicker, setShowSpImagePicker, handleSpPickExistingImage,
        spImageHeight, setSpImageHeight,
        spLoadingImage, setSpLoadingImage,
        handleSaveSupplierProduct,
        // Units
        orderUnits, billingUnits, invoiceUnits,
        newOrderUnit, setNewOrderUnit,
        newBillingUnit, setNewBillingUnit,
        newInvoiceUnit, setNewInvoiceUnit,
        orderUnitPopoverOpen, setOrderUnitPopoverOpen,
        billingUnitPopoverOpen, setBillingUnitPopoverOpen,
        invoiceUnitPopoverOpen, setInvoiceUnitPopoverOpen,
        handleAddOrderUnit, handleRemoveOrderUnit,
        handleAddBillingUnit, handleRemoveBillingUnit,
        handleAddInvoiceUnit, handleRemoveInvoiceUnit,
        // Delete dialog
        deleteDialog, setDeleteDialog,
        deleteType,
        handleConfirmDelete,
        // SP Detail popup
        spDetailProduct, setSpDetailProduct,
        // Permissions
        canToggleCommandable,
    } = props;

    return (
        <>
{/* Industrial Product Dialog */}
<Dialog open={productDialog} onOpenChange={setProductDialog}>
    <DialogContent>
        <DialogHeader>
            <DialogTitle>
                {editingProduct ? 'Modifier le produit' : 'Nouveau produit industriel'}
            </DialogTitle>
            <DialogDescription>
                {editingProduct ? 'Modifiez les informations du produit' : 'Créez un nouveau produit industriel'}
            </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
            <div className="space-y-1">
                <Label className="text-xs">Nom *</Label>
                <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    placeholder="Nom du produit"
                    className="h-9"
                    data-testid="product-name-input"
                />
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                    <Label className="text-xs">Catégorie</Label>
                    {!showNewCategoryInput ? (
                        <Select
                            value={productForm.category || '__none__'}
                            onValueChange={(value) => {
                                if (value === '__new__') {
                                    setShowNewCategoryInput(true);
                                    setNewCategoryName('');
                                } else if (value === '__none__') {
                                    setProductForm({ ...productForm, category: '' });
                                } else {
                                    setProductForm({ ...productForm, category: value });
                                }
                            }}
                        >
                            <SelectTrigger data-testid="product-category-select">
                                <SelectValue placeholder="Sélectionner une catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">Sans catégorie</SelectItem>
                                {categories.map(cat => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                                <SelectItem value="__new__" className="text-blue-600 font-medium">
                                    + Ajouter une nouvelle catégorie
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="flex gap-2">
                            <Input
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="Nom de la nouvelle catégorie"
                                data-testid="new-category-input"
                                autoFocus
                            />
                            <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                    if (newCategoryName.trim()) {
                                        const trimmedName = newCategoryName.trim();
                                        setProductForm({ ...productForm, category: trimmedName });
                                        // Ajouter la nouvelle catégorie à la liste si elle n'existe pas
                                        if (!categories.includes(trimmedName)) {
                                            setCategories(prev => [...prev, trimmedName].sort());
                                        }
                                        setShowNewCategoryInput(false);
                                    }
                                }}
                                className="bg-emerald-500 hover:bg-emerald-600"
                                data-testid="confirm-new-category-btn"
                            >
                                OK
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setShowNewCategoryInput(false);
                                    setNewCategoryName('');
                                }}
                                data-testid="cancel-new-category-btn"
                            >
                                ✕
                            </Button>
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Unité</Label>
                    <Select
                        value={productForm.unit}
                        onValueChange={(value) => setProductForm({ ...productForm, unit: value })}
                    >
                        <SelectTrigger data-testid="product-unit-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="pièce">Pièce</SelectItem>
                            <SelectItem value="kg">Kilogramme</SelectItem>
                            <SelectItem value="litre">Litre</SelectItem>
                            <SelectItem value="mètre">Mètre</SelectItem>
                            <SelectItem value="boîte">Boîte</SelectItem>
                            <SelectItem value="carton">Carton</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">TVA</Label>
                    <Select
                        value={String(productForm.vat_rate ?? 2.1)}
                        onValueChange={(value) => setProductForm({ ...productForm, vat_rate: parseFloat(value) })}
                    >
                        <SelectTrigger data-testid="product-vat-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">0 %</SelectItem>
                            <SelectItem value="2.1">2,1 %</SelectItem>
                            <SelectItem value="8.5">8,5 %</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <Label className="text-xs">Famille</Label>
                    <Popover modal={true} open={famillePopoverOpen} onOpenChange={setFamillePopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button 
                                variant="outline" 
                                className="w-full justify-between"
                                data-testid="product-famille-select"
                            >
                                {productForm.famille || 'Sélectionner une famille'}
                                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="start">
                            <div className="p-2 border-b">
                                <div className="flex gap-2">
                                    <Input
                                        value={newFamilleName}
                                        onChange={(e) => setNewFamilleName(e.target.value)}
                                        placeholder="Nouvelle famille..."
                                        className="h-8 text-sm"
                                        onKeyDown={async (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const name = newFamilleName.trim();
                                                if (!name) return;
                                                try {
                                                    await industrialProductsAPI.addFamille(name);
                                                    setFamilles(prev => Array.from(new Set([...prev, name])).sort());
                                                    setProductForm({ ...productForm, famille: name });
                                                    setNewFamilleName('');
                                                    toast.success(`Famille "${name}" ajoutée`);
                                                } catch (error) {
                                                    toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de la famille');
                                                }
                                            }
                                        }}
                                    />
                                    <Button 
                                        type="button"
                                        size="sm"
                                        onClick={async () => {
                                            const name = newFamilleName.trim();
                                            if (!name) return;
                                            try {
                                                await industrialProductsAPI.addFamille(name);
                                                setFamilles(prev => Array.from(new Set([...prev, name])).sort());
                                                setProductForm({ ...productForm, famille: name });
                                                setNewFamilleName('');
                                                toast.success(`Famille "${name}" ajoutée`);
                                            } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de la famille');
                                            }
                                        }}
                                        className="bg-emerald-500 hover:bg-emerald-600"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="max-h-60 overflow-auto p-1" onWheel={(e) => e.stopPropagation()}>
                                <div
                                    className="flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100 rounded"
                                    onClick={() => {
                                        setProductForm({ ...productForm, famille: '' });
                                        setFamillePopoverOpen(false);
                                    }}
                                >
                                    <span className="text-slate-500 italic">Sans famille</span>
                                </div>
                                {familles.map((famille) => (
                                    <div
                                        key={famille}
                                        className="flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100 rounded group"
                                    >
                                        <span
                                            className="flex-1"
                                            onClick={() => {
                                                setProductForm({ ...productForm, famille });
                                                setFamillePopoverOpen(false);
                                            }}
                                        >
                                            {famille}
                                        </span>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    await industrialProductsAPI.deleteFamille(famille);
                                                    setFamilles(prev => prev.filter(f => f !== famille));
                                                    if (productForm.famille === famille) {
                                                        setProductForm({ ...productForm, famille: '' });
                                                    }
                                                    toast.success(`Famille "${famille}" supprimée`);
                                                } catch (error) {
                                                    toast.error('Erreur lors de la suppression');
                                                }
                                            }}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        placeholder="Description optionnelle"
                        data-testid="product-description-input"
                    />
                </div>
            </div>
            {/* Allergènes - visible uniquement pour les familles alimentaires */}
            {isFoodFamily(productForm.famille) && (
            <div className="border rounded-lg p-3 space-y-2 bg-orange-50/50">
                <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-slate-700">Allergènes</Label>
                    <button
                        type="button"
                        onClick={() => setHideUnselectedAllergens(!hideUnselectedAllergens)}
                        className="p-1 rounded hover:bg-orange-100 text-slate-400 hover:text-slate-600 transition-colors"
                        title={hideUnselectedAllergens ? "Afficher tous les allergènes" : "Masquer les non sélectionnés"}
                        data-testid="toggle-allergens-visibility"
                    >
                        {hideUnselectedAllergens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {EU_ALLERGENS.map((allergen) => {
                        const isSelected = (productForm.allergens || []).includes(allergen);
                        if (hideUnselectedAllergens && !isSelected) return null;
                        return (
                            <button
                                key={allergen}
                                type="button"
                                onClick={() => {
                                    const current = productForm.allergens || [];
                                    setProductForm({
                                        ...productForm,
                                        allergens: isSelected
                                            ? current.filter(a => a !== allergen)
                                            : [...current, allergen]
                                    });
                                }}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                                    isSelected
                                        ? 'bg-orange-500 text-white border-orange-500'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
                                }`}
                                data-testid={`allergen-ip-${allergen.toLowerCase().replace(/\s/g, '-')}`}
                            >
                                {allergen}
                            </button>
                        );
                    })}
                    {hideUnselectedAllergens && (productForm.allergens || []).length === 0 && (
                        <span className="text-xs text-slate-400 italic">Aucun allergène sélectionné</span>
                    )}
                </div>
            </div>
            )}
            {/* Photo */}
            <div className="flex items-center gap-3">
                <Label className="text-xs">Photo</Label>
                {imagePreview ? (
                    <div className="relative w-16 h-16 rounded overflow-hidden border border-slate-200 flex-shrink-0">
                        <img src={imagePreview} alt="" className="w-full h-full object-contain" />
                        <button
                            type="button"
                            onClick={handleImageRemove}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                            data-testid="remove-product-image-btn"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ) : (
                    <div className="w-16 h-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 flex-shrink-0">
                        <Camera className="w-5 h-5" />
                    </div>
                )}
                <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} data-testid="product-image-input" />
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs font-medium text-slate-700 transition-colors">
                        <Camera className="w-3.5 h-3.5" />
                        {imagePreview ? 'Changer' : 'Ajouter'}
                    </span>
                </label>
                <button
                    type="button"
                    onClick={() => setShowImagePicker(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs font-medium text-slate-700 transition-colors"
                    data-testid="product-pick-existing-image-btn"
                    title="Copier la photo d'un autre produit"
                >
                    <Images className="w-3.5 h-3.5" />
                    Choisir existante
                </button>
                <PhoneCaptureButton
                    size="sm"
                    className="h-auto px-2 py-1 text-xs"
                    label="Téléphone"
                    multiple={false}
                    testId="product-image-phone-btn"
                    onPhotos={(fs) => fs[0] && handleImageSelect({ target: { files: [fs[0]], value: '' } })}
                />
            </div>
        </div>
        {canEditCompanies && (
            <div className="space-y-2 border-t pt-4">
                <h3 className="font-semibold text-slate-700 text-sm">Utilisable par</h3>
                <p className="text-xs text-slate-500">
                    Entreprises pour lesquelles ce produit est disponible dans les commandes et réceptions.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                        { key: 'fuji_express', label: 'Fuji Express' },
                        { key: 'concept_lab', label: 'Concept Lab' },
                        { key: 'my_food', label: 'My Food' },
                        { key: 'boucherie', label: 'Boucherie' },
                    ].map(({ key, label }) => (
                        <label
                            key={key}
                            className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm"
                            data-testid={`product-company-${key}`}
                        >
                            <input
                                type="checkbox"
                                checked={productForm[key] !== false}
                                onChange={(e) => setProductForm({ ...productForm, [key]: e.target.checked })}
                                className="rounded"
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>
            </div>
        )}
        <DialogFooter>
            <Button variant="outline" onClick={() => { setProductDialog(false); setShowNewCategoryInput(false); setShowNewFamilleInput(false); }}>
                Annuler
            </Button>
            <Button onClick={handleSaveProduct} className="btn-primary" data-testid="save-product-btn">
                {editingProduct ? 'Enregistrer' : 'Créer'}
            </Button>
        </DialogFooter>
    </DialogContent>
</Dialog>

{/* Image Crop Dialog */}
<ImageCropDialog
    open={showCropDialog}
    onClose={() => setShowCropDialog(false)}
    imageSrc={cropImageSrc}
    onCropped={handleCropDone}
/>

{/* Existing Image Picker (Industrial) */}
<ImagePickerDialog
    open={showImagePicker}
    onClose={() => setShowImagePicker(false)}
    onPick={handlePickExistingImage}
    excludeId={editingProduct?.id}
    excludeType="industrial"
/>

{/* Supplier Product Dialog */}
<Dialog open={supplierProductDialog} onOpenChange={setSupplierProductDialog}>
    <DialogContent className="max-w-2xl">
        {spLoadingImage && (
            <div className="absolute inset-0 bg-white/90 z-50 flex items-center justify-center rounded-lg">
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-500">Chargement de l'image...</span>
                </div>
            </div>
        )}
        <DialogHeader>
            <DialogTitle>
                {editingSupplierProduct ? 'Modifier le produit fournisseur' : 'Ajouter un produit fournisseur'}
            </DialogTitle>
            <DialogDescription>
                {editingSupplierProduct ? 'Modifiez les informations' : 'Associez ce produit à un fournisseur'}
            </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Photo */}
            <div>
                <Label className="mb-2 block">Photo</Label>
                <div className="flex items-start gap-4">
                    {spImagePreview ? (
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
                            <img src={spImagePreview} alt="Produit" className="w-full h-full object-cover" />
                            <button
                                type="button"
                                onClick={() => { setSpImagePreview(null); setSpImageFile(null); setSpImageDeleted(true); setSpImageSourceRef(null); }}
                                className="absolute top-1 right-1 bg-black/50 rounded-full p-0.5 text-white hover:bg-black/70"
                                data-testid="sp-remove-image"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ) : (
                        <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 flex-shrink-0">
                            <ImageIcon className="w-8 h-8 text-slate-300" />
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id="sp-file-input"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setSpLoadingImage(true);
                                    const url = URL.createObjectURL(file);
                                    const img = new Image();
                                    img.onload = () => {
                                        setSpCropImageSrc(url);
                                        setSpShowCropDialog(true);
                                        setTimeout(() => setSpLoadingImage(false), 300);
                                    };
                                    img.src = url;
                                }
                                e.target.value = '';
                            }}
                            data-testid="sp-image-file-input"
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('sp-file-input')?.click()}>
                            <ImageIcon className="w-4 h-4 mr-1" /> Choisir un fichier
                        </Button>
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            id="sp-camera-input"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setSpLoadingImage(true);
                                    const url = URL.createObjectURL(file);
                                    const img = new Image();
                                    img.onload = () => {
                                        setSpCropImageSrc(url);
                                        setSpShowCropDialog(true);
                                        setTimeout(() => setSpLoadingImage(false), 300);
                                    };
                                    img.src = url;
                                }
                                e.target.value = '';
                            }}
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('sp-camera-input')?.click()}>
                            <Camera className="w-4 h-4 mr-1" /> Prendre une photo
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setShowSpImagePicker(true)} data-testid="sp-pick-existing-image-btn" title="Copier la photo d'un autre produit">
                            <Images className="w-4 h-4 mr-1" /> Choisir existante
                        </Button>
                        <div className="flex items-center gap-2 mt-1">
                            <label className="text-xs text-slate-500 whitespace-nowrap">Hauteur :</label>
                            <input
                                type="number"
                                placeholder="Auto"
                                value={spImageHeight}
                                onChange={(e) => setSpImageHeight(e.target.value)}
                                className="w-16 text-xs border border-slate-200 rounded px-1.5 py-0.5 text-center"
                                min={50}
                                data-testid="sp-image-height-input"
                            />
                            <span className="text-[10px] text-slate-400">px</span>
                        </div>
                    </div>
                </div>
                <ImageCropDialog
                    open={spShowCropDialog}
                    onClose={() => { setSpShowCropDialog(false); setSpCropImageSrc(null); }}
                    imageSrc={spCropImageSrc}
                    onCropped={(croppedFile, previewUrl, usedHeight) => {
                        setSpImageFile(croppedFile);
                        setSpImagePreview(previewUrl);
                        setSpImageDeleted(false);
                        setSpImageSourceRef(null);
                        setSpCropImageSrc(null);
                        if (usedHeight) setSpImageHeight(String(usedHeight));
                    }}
                    defaultMaxHeight={spImageHeight ? parseInt(spImageHeight, 10) : null}
                />
                <ImagePickerDialog
                    open={showSpImagePicker}
                    onClose={() => setShowSpImagePicker(false)}
                    onPick={handleSpPickExistingImage}
                    excludeId={editingSupplierProduct?.id}
                    excludeType="supplier"
                    currentIndustrialProductId={editingSupplierProduct?.industrial_product_id || supplierProductForm?.industrial_product_id || null}
                />
            </div>

            {/* Fournisseur + Commandable */}
            <div className="flex gap-4 items-end">
                <div className="space-y-2 flex-1 min-w-0">
                    <Label>Fournisseur *</Label>
                    <Select
                        value={supplierProductForm.supplier_id}
                        onValueChange={(value) => setSupplierProductForm({ ...supplierProductForm, supplier_id: value })}
                    >
                        <SelectTrigger data-testid="supplier-select">
                            <SelectValue placeholder="Sélectionner un fournisseur" />
                        </SelectTrigger>
                        <SelectContent>
                            {[...suppliers].sort((a, b) => a.name.localeCompare(b.name, 'fr')).map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <label
                    className={`flex items-center gap-2 px-3 h-10 rounded-lg border text-xs font-medium shrink-0 cursor-pointer select-none transition-colors ${
                        supplierProductForm.commandable !== false
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : 'bg-red-50 border-red-200 text-red-600'
                    } ${!canToggleCommandable ? 'opacity-60 cursor-default' : ''}`}
                >
                    <input
                        type="checkbox"
                        checked={supplierProductForm.commandable !== false}
                        disabled={!canToggleCommandable}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, commandable: e.target.checked })}
                        className="rounded border-slate-300"
                        data-testid="checkbox-commandable"
                    />
                    Commandable
                </label>
            </div>

            {/* Référence et désignation */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Référence fournisseur *</Label>
                    <Input
                        value={supplierProductForm.supplier_ref}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, supplier_ref: e.target.value })}
                        placeholder="REF-123"
                        data-testid="supplier-ref-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Désignation fournisseur</Label>
                    <Input
                        value={supplierProductForm.supplier_name_product}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, supplier_name_product: e.target.value })}
                        placeholder="Nom du produit chez le fournisseur"
                        data-testid="supplier-product-name-input"
                    />
                </div>
            </div>

            {/* Marque */}
            <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                    value={supplierProductForm.brand}
                    onChange={(e) => setSupplierProductForm({ ...supplierProductForm, brand: e.target.value })}
                    placeholder="Ex: Diversey, Suma, Ecover..."
                    data-testid="brand-input"
                />
            </div>

            {/* Section Unités de commande/facturation */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <h4 className="font-medium text-slate-700">Unités de commande et facturation</h4>
                
                {/* Checkboxes */}
                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="sell_by_detail"
                            checked={supplierProductForm.sell_by_detail}
                            onChange={(e) => setSupplierProductForm({ 
                                ...supplierProductForm, 
                                sell_by_detail: e.target.checked
                            })}
                            className="rounded border-slate-300"
                        />
                        <Label htmlFor="sell_by_detail" className="text-sm text-slate-600 cursor-pointer">
                            Vente au détail possible (le colisage reste indicatif)
                        </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="variable_weight"
                            checked={supplierProductForm.variable_weight}
                            onChange={(e) => setSupplierProductForm({ 
                                ...supplierProductForm, 
                                variable_weight: e.target.checked
                            })}
                            className="rounded border-slate-300"
                        />
                        <Label htmlFor="variable_weight" className="text-sm text-slate-600 cursor-pointer">
                            Poids variable (saisie du poids réel à la réception)
                        </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="unknown_packaging"
                            checked={supplierProductForm.unknown_packaging}
                            onChange={(e) => setSupplierProductForm({ 
                                ...supplierProductForm, 
                                unknown_packaging: e.target.checked
                            })}
                            className="rounded border-slate-300"
                        />
                        <Label htmlFor="unknown_packaging" className="text-sm text-slate-600 cursor-pointer">
                            Colisage inconnu (seule l'unité de facturation sera utilisée)
                        </Label>
                    </div>
                </div>

                {/* Unité de facturation - toujours visible, en premier */}
                <div className="space-y-1.5">
                    <Label className="text-sm">Unité de facturation {supplierProductForm.unknown_packaging && <span className="text-xs text-amber-600 font-medium">(utilisée pour commande et réception)</span>}</Label>
                    <div className="flex gap-1">
                        <Select
                            value={supplierProductForm.invoice_unit}
                            onValueChange={(value) => setSupplierProductForm({ ...supplierProductForm, invoice_unit: value })}
                        >
                            <SelectTrigger data-testid="invoice-unit-select" className="w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[...invoiceUnits].sort((a, b) => a.localeCompare(b, 'fr')).map((unit) => (
                                    <SelectItem key={unit} value={unit}>
                                        {unit}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Popover modal={true} open={invoiceUnitPopoverOpen} onOpenChange={setInvoiceUnitPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" title="Gérer les unités">
                                    <PlusCircle className="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-3">
                                <div className="space-y-3">
                                    <Label className="text-sm font-medium">Gérer les unités de facturation</Label>
                                    <div className="space-y-1">
                                        <span className="text-xs text-slate-500">Unités disponibles :</span>
                                        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
                                            {[...invoiceUnits].sort((a, b) => a.localeCompare(b, 'fr')).map(unit => (
                                                <span key={unit} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                                                    {unit}
                                                    <button onClick={() => handleRemoveInvoiceUnit(unit)} className="text-red-500 hover:text-red-700" title="Supprimer">
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="border-t pt-2 space-y-2">
                                        <span className="text-xs text-slate-500">Ajouter une unité :</span>
                                        <Input value={newInvoiceUnit} onChange={(e) => setNewInvoiceUnit(e.target.value)} placeholder="Ex: kg, carton..." onKeyDown={(e) => e.key === 'Enter' && handleAddInvoiceUnit()} />
                                        <Button onClick={handleAddInvoiceUnit} size="sm" className="w-full">Ajouter</Button>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
                
                {/* Colisage - format phrase : [Unité commande] de [qté] [Unité colisage] */}
                {!supplierProductForm.unknown_packaging && (
                <div className="space-y-1.5">
                    <Label className="text-sm">
                        Colisage {supplierProductForm.sell_by_detail && <span className="text-xs text-slate-400">(indicatif)</span>}
                    </Label>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Unité de commande */}
                        <div className="flex gap-1 items-center">
                            <Select
                                value={supplierProductForm.order_unit}
                                onValueChange={(value) => setSupplierProductForm({ ...supplierProductForm, order_unit: value })}
                            >
                                <SelectTrigger data-testid="order-unit-select" className="w-28 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[...orderUnits].sort((a, b) => a.localeCompare(b, 'fr')).map((unit) => (
                                        <SelectItem key={unit} value={unit}>
                                            {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Popover modal={true} open={orderUnitPopoverOpen} onOpenChange={setOrderUnitPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" title="Gérer les unités">
                                        <PlusCircle className="h-3.5 w-3.5" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-3">
                                    <div className="space-y-3">
                                        <Label className="text-sm font-medium">Gérer les unités de commande</Label>
                                        <div className="space-y-1">
                                            <span className="text-xs text-slate-500">Unités disponibles :</span>
                                            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
                                                {[...orderUnits].sort((a, b) => a.localeCompare(b, 'fr')).map(unit => (
                                                    <span key={unit} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                                                        {unit}
                                                        <button onClick={() => handleRemoveOrderUnit(unit)} className="text-red-500 hover:text-red-700" title="Supprimer">
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="border-t pt-2 space-y-2">
                                            <span className="text-xs text-slate-500">Ajouter une unité :</span>
                                            <Input value={newOrderUnit} onChange={(e) => setNewOrderUnit(e.target.value)} placeholder="Ex: fût, bac..." onKeyDown={(e) => e.key === 'Enter' && handleAddOrderUnit()} />
                                            <Button onClick={handleAddOrderUnit} size="sm" className="w-full">Ajouter</Button>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <span className="text-sm text-slate-500 font-medium">de</span>

                        {/* Quantité */}
                        <Input
                            type="number"
                            step="0.01"
                            value={supplierProductForm.packaging_qty}
                            onChange={(e) => setSupplierProductForm({ ...supplierProductForm, packaging_qty: e.target.value })}
                            placeholder="Qté"
                            className="w-20 h-9 text-center"
                            data-testid="packaging-qty-input"
                        />

                        {/* Unité de colisage */}
                        <div className="flex gap-1 items-center">
                            <Select
                                value={supplierProductForm.billing_unit}
                                onValueChange={(value) => setSupplierProductForm({ ...supplierProductForm, billing_unit: value })}
                            >
                                <SelectTrigger data-testid="billing-unit-select" className="w-28 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[...billingUnits].sort((a, b) => a.localeCompare(b, 'fr')).map((unit) => (
                                        <SelectItem key={unit} value={unit}>
                                            {unit}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Popover modal={true} open={billingUnitPopoverOpen} onOpenChange={setBillingUnitPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" title="Gérer les unités">
                                        <PlusCircle className="h-3.5 w-3.5" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-3">
                                    <div className="space-y-3">
                                        <Label className="text-sm font-medium">Gérer les unités de colisage</Label>
                                        <div className="space-y-1">
                                            <span className="text-xs text-slate-500">Unités disponibles :</span>
                                            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
                                                {[...billingUnits].sort((a, b) => a.localeCompare(b, 'fr')).map(unit => (
                                                    <span key={unit} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                                                        {unit}
                                                        <button onClick={() => handleRemoveBillingUnit(unit)} className="text-red-500 hover:text-red-700" title="Supprimer">
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="border-t pt-2 space-y-2">
                                            <span className="text-xs text-slate-500">Ajouter une unité :</span>
                                            <Input value={newBillingUnit} onChange={(e) => setNewBillingUnit(e.target.value)} placeholder="Ex: sachet, barquette..." onKeyDown={(e) => e.key === 'Enter' && handleAddBillingUnit()} />
                                            <Button onClick={handleAddBillingUnit} size="sm" className="w-full">Ajouter</Button>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </div>
                )}

                {/* Exemple explicatif */}
                {supplierProductForm.packaging_qty && !supplierProductForm.sell_by_detail && !supplierProductForm.unknown_packaging && (
                    <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-700">
                        <strong>Exemple :</strong> Commander 2 {supplierProductForm.order_unit}(s) = {2 * parseFloat(supplierProductForm.packaging_qty || 0)} {supplierProductForm.billing_unit}. Facturé en : {supplierProductForm.invoice_unit}
                    </div>
                )}
                {supplierProductForm.sell_by_detail && (
                    <div className="p-3 bg-amber-50 rounded-md text-sm text-amber-700">
                        <strong>Vente au détail :</strong> L'unité de commande sera directement l'unité de colisage ({supplierProductForm.billing_unit}).
                        {supplierProductForm.packaging_qty && supplierProductForm.order_unit && (
                            <span> Colisage indicatif : {supplierProductForm.order_unit} de {supplierProductForm.packaging_qty} {supplierProductForm.billing_unit}.</span>
                        )}
                    </div>
                )}
            </div>

            {/* Prix */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Prix unitaire (€/{supplierProductForm.invoice_unit}) *</Label>
                    <Input
                        type="number"
                        step="0.01"
                        value={supplierProductForm.unit_price}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, unit_price: e.target.value })}
                        placeholder="0.00"
                        data-testid="unit-price-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Qté minimum de commande</Label>
                    <Input
                        type="number"
                        value={supplierProductForm.min_order_qty}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, min_order_qty: parseInt(e.target.value) || 1 })}
                        data-testid="min-qty-input"
                    />
                </div>
            </div>

            {/* Prix calculé par unité de commande (hide when packaging unknown) */}
            {supplierProductForm.unit_price && supplierProductForm.packaging_qty && !supplierProductForm.sell_by_detail && !supplierProductForm.unknown_packaging && (
                <div className="p-3 bg-emerald-50 rounded-md">
                    <p className="text-sm text-emerald-700">
                        <span className="font-medium">Prix par {supplierProductForm.order_unit} : </span>
                        <span className="text-emerald-900 font-semibold">
                            {(parseFloat(supplierProductForm.unit_price) * parseFloat(supplierProductForm.packaging_qty)).toFixed(2)}€
                        </span>
                        <span className="text-emerald-600 ml-2">
                            ({supplierProductForm.packaging_qty} {supplierProductForm.billing_unit} × {supplierProductForm.unit_price}€/{supplierProductForm.invoice_unit})
                        </span>
                    </p>
                </div>
            )}

            {/* Contenance du produit */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Contenance (valeur)</Label>
                    <Input
                        type="number"
                        step="0.01"
                        value={supplierProductForm.capacity_value}
                        onChange={(e) => setSupplierProductForm({ ...supplierProductForm, capacity_value: e.target.value })}
                        placeholder="Ex: 1.5, 500, 1"
                        data-testid="capacity-value-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Unité contenance</Label>
                    <Select
                        value={supplierProductForm.capacity_unit}
                        onValueChange={(value) => setSupplierProductForm({ ...supplierProductForm, capacity_unit: value })}
                    >
                        <SelectTrigger data-testid="capacity-unit-select">
                            <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="L">Litre (L)</SelectItem>
                            <SelectItem value="mL">Millilitre (mL)</SelectItem>
                            <SelectItem value="kg">Kilogramme (kg)</SelectItem>
                            <SelectItem value="g">Gramme (g)</SelectItem>
                            <SelectItem value="pièce">Pièce</SelectItem>
                            <SelectItem value="unité">Unité</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
        {/* Allergènes fournisseur - héritage ou surcharge */}
        {(() => {
            const parentProduct = products.find(p => p.id === supplierProductForm.industrial_product_id);
            if (!parentProduct || !isFoodFamily(parentProduct.famille)) return null;
            const parentAllergens = parentProduct.allergens || [];
            const spAllergens = supplierProductForm.allergens;
            const effectiveAllergens = spAllergens !== null && spAllergens !== undefined ? spAllergens : parentAllergens;
            const isInherited = spAllergens === null || spAllergens === undefined;
            return (
                <div className="border rounded-lg p-3 space-y-2 bg-orange-50/50 mx-6 mb-2">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-slate-700">Allergènes</Label>
                        <div className="flex items-center gap-2">
                            {isInherited ? (
                                <span className="text-[10px] text-orange-500 font-medium px-1.5 py-0.5 bg-orange-100 rounded">Hérité du produit industriel</span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setSupplierProductForm({ ...supplierProductForm, allergens: null })}
                                    className="text-[10px] text-blue-500 hover:text-blue-700 underline"
                                >
                                    Réinitialiser (hériter)
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setHideUnselectedAllergens(!hideUnselectedAllergens)}
                                className="p-1 rounded hover:bg-orange-100 text-slate-400 hover:text-slate-600 transition-colors"
                                title={hideUnselectedAllergens ? "Afficher tous les allergènes" : "Masquer les non sélectionnés"}
                            >
                                {hideUnselectedAllergens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {EU_ALLERGENS.map((allergen) => {
                            const isSelected = effectiveAllergens.includes(allergen);
                            if (hideUnselectedAllergens && !isSelected) return null;
                            return (
                                <button
                                    key={allergen}
                                    type="button"
                                    onClick={() => {
                                        const current = [...effectiveAllergens];
                                        const updated = isSelected
                                            ? current.filter(a => a !== allergen)
                                            : [...current, allergen];
                                        setSupplierProductForm({ ...supplierProductForm, allergens: updated });
                                    }}
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                                        isSelected
                                            ? 'bg-orange-500 text-white border-orange-500'
                                            : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
                                    }`}
                                    data-testid={`allergen-sp-${allergen.toLowerCase().replace(/\s/g, '-')}`}
                                >
                                    {allergen}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        })()}
        <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierProductDialog(false)}>
                Annuler
            </Button>
            <Button onClick={handleSaveSupplierProduct} className="btn-primary" data-testid="save-supplier-product-btn">
                {editingSupplierProduct ? 'Enregistrer' : 'Ajouter'}
            </Button>
        </DialogFooter>
    </DialogContent>
</Dialog>

{/* Delete Confirmation Dialog */}
<AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
    <AlertDialogContent>
        <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
                {deleteType === 'product' 
                    ? 'Cette action supprimera le produit et tous ses produits fournisseurs associés.'
                    : 'Cette action supprimera ce produit fournisseur.'}
            </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
                Supprimer
            </AlertDialogAction>
        </AlertDialogFooter>
    </AlertDialogContent>
</AlertDialog>

{/* Supplier Product Detail Popup */}
<Dialog open={!!spDetailProduct} onOpenChange={(open) => { if (!open) setSpDetailProduct(null); }}>
    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sp-detail-dialog">
        <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-600" />
                {spDetailProduct?.supplier_name_product || 'Produit fournisseur'}
            </DialogTitle>
            <DialogDescription>
                {spDetailProduct?.supplier_name}
            </DialogDescription>
        </DialogHeader>
        {spDetailProduct && (
            <div className="space-y-4 py-2">
                {/* Photo */}
                <div className="flex justify-center">
                    {spDetailProduct.image_url ? (
                        <div className="rounded-lg overflow-hidden border border-slate-200 bg-white" style={{ maxHeight: spDetailProduct.image_height ? `${spDetailProduct.image_height}px` : '144px' }}>
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${spDetailProduct.image_url}`}
                                alt={spDetailProduct.supplier_name_product}
                                className="max-w-full h-full object-contain mx-auto"
                                style={{ maxHeight: spDetailProduct.image_height ? `${spDetailProduct.image_height}px` : '144px' }}
                                data-testid="sp-detail-image"
                            />
                        </div>
                    ) : (
                        <div className="w-48 h-36 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50">
                            <Package className="w-12 h-12 text-slate-300" />
                        </div>
                    )}
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div className="col-span-2 grid grid-cols-3 gap-x-4 items-start">
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Fournisseur</p>
                            <p className="font-medium text-slate-900">{spDetailProduct.supplier_name}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Référence</p>
                            <p className="font-mono text-slate-700">{spDetailProduct.supplier_ref || '-'}</p>
                        </div>
                        <div className="flex items-center justify-end">
                            <span
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium select-none ${
                                    spDetailProduct.commandable !== false
                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                        : 'bg-red-50 border-red-200 text-red-600'
                                }`}
                                data-testid="sp-detail-commandable-badge"
                            >
                                <input
                                    type="checkbox"
                                    checked={spDetailProduct.commandable !== false}
                                    disabled
                                    className="rounded border-slate-300 pointer-events-none"
                                />
                                Commandable
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Désignation fournisseur</p>
                        <p className="font-medium text-slate-900">{spDetailProduct.supplier_name_product || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Marque</p>
                        <p className="text-slate-700">{spDetailProduct.brand || '-'}</p>
                    </div>
                    {(() => {
                        const parentProduct = products.find(p => p.id === spDetailProduct.industrial_product_id);
                        const parentAllergens = parentProduct?.allergens || [];
                        const effectiveAllergens = spDetailProduct.allergens !== null && spDetailProduct.allergens !== undefined
                            ? spDetailProduct.allergens
                            : parentAllergens;
                        if (effectiveAllergens.length === 0) return null;
                        return (
                            <div className="col-span-2">
                                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Allergènes</p>
                                <div className="flex flex-wrap gap-1">
                                    {effectiveAllergens.map((a) => (
                                        <span key={a} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full border border-orange-200">
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Unité de facturation</p>
                        <p className="text-slate-700">{spDetailProduct.invoice_unit || spDetailProduct.billing_unit || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Colisage</p>
                        <p className="text-slate-700">
                            {spDetailProduct.packaging_qty && spDetailProduct.order_unit && spDetailProduct.billing_unit
                                ? `${spDetailProduct.order_unit} de ${spDetailProduct.packaging_qty} ${spDetailProduct.billing_unit}`
                                : '-'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Conditionnement de vente</p>
                        <p className="text-slate-700">
                            {spDetailProduct.sell_by_detail
                                ? (spDetailProduct.billing_unit || 'pièce')
                                : spDetailProduct.packaging_qty && spDetailProduct.order_unit && spDetailProduct.billing_unit
                                    ? `${spDetailProduct.order_unit} de ${spDetailProduct.packaging_qty} ${spDetailProduct.billing_unit}`
                                    : (spDetailProduct.order_unit || '-')}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Contenance</p>
                        <p className="text-slate-700">
                            {spDetailProduct.capacity_value && spDetailProduct.capacity_unit
                                ? `${spDetailProduct.capacity_value} ${spDetailProduct.capacity_unit}`
                                : '-'}
                        </p>
                    </div>
                    {(() => {
                        const parent = products.find(p => p.id === spDetailProduct.industrial_product_id);
                        const effectiveVat = spDetailProduct.vat_rate ?? parent?.vat_rate ?? 2.1;
                        return (
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">TVA</p>
                                <p className="text-slate-700">{String(effectiveVat).replace('.', ',')} %</p>
                            </div>
                        );
                    })()}
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Prix unitaire</p>
                        <p className="font-semibold text-emerald-600 text-base">
                            {spDetailProduct.unit_price
                                ? `${formatCurrency(spDetailProduct.unit_price)} / ${spDetailProduct.invoice_unit || spDetailProduct.billing_unit || 'unité'}`
                                : '-'}
                        </p>
                    </div>
                    {spDetailProduct.unit_price && spDetailProduct.capacity_value && spDetailProduct.capacity_value > 0 && (
                        <div className="col-span-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                            <p className="text-xs text-emerald-600 uppercase tracking-wider">Tarif / Contenance</p>
                            <p className="font-bold text-emerald-700 text-lg">
                                {formatCurrency(spDetailProduct.unit_price / spDetailProduct.capacity_value)} / {spDetailProduct.capacity_unit || 'u'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )}
        {spDetailProduct && (
            <PriceHistoryPanel supplierProductId={spDetailProduct.id} />
        )}
        <div className="flex justify-end pt-2 sm:hidden">
            <Button variant="outline" size="sm" onClick={() => setSpDetailProduct(null)} data-testid="sp-detail-close-btn">
                Fermer
            </Button>
        </div>
    </DialogContent>
</Dialog>
        </>
    );
}
